import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Save, Copy, ExternalLink, Percent, Store, Plus, Trash2, RefreshCw } from "lucide-react";

export default function DealsConfigPage() {
  const [rows, setRows] = useState([]);
  const [editing, setEditing] = useState(null);
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try { const r = await api.get("/deals/config"); setRows(r.data); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const openEdit = (row) => {
    setEditing({
      branch_id: row.id,
      branch_name: row.name,
      branch_code: row.code,
      enabled: row.config?.enabled !== false,
      tiers: (row.config?.tiers || []).map((t) => ({ ...t })),
      min_qty: row.config?.min_qty || 1,
      note: row.config?.note || "",
    });
  };

  const save = async () => {
    setBusy(true);
    try {
      await api.patch(`/deals/config/${editing.branch_id}`, {
        enabled: editing.enabled,
        tiers: editing.tiers.map((t) => ({ max_hours: Number(t.max_hours), discount_pct: Number(t.discount_pct), label: t.label })),
        min_qty: Number(editing.min_qty) || 1,
        note: editing.note || null,
      });
      toast.success("Config saved");
      setEditing(null); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  const publicUrl = (code) => `${window.location.origin}/deals/${code}`;

  const copyUrl = (code) => {
    navigator.clipboard.writeText(publicUrl(code)).then(() => toast.success("Public link copied"));
  };

  const updateTier = (idx, patch) => setEditing({ ...editing, tiers: editing.tiers.map((t, i) => i === idx ? { ...t, ...patch } : t) });
  const addTier = () => setEditing({ ...editing, tiers: [...editing.tiers, { max_hours: 96, discount_pct: 10, label: "" }] });
  const removeTier = (idx) => setEditing({ ...editing, tiers: editing.tiers.filter((_, i) => i !== idx) });

  return (
    <div className="space-y-6" data-testid="deals-config-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <div className="uc-label">Sales · Fresh Deals</div>
          <h1 className="font-[Outfit] text-3xl sm:text-4xl font-semibold tracking-tight mt-1">Today's Deals — Configuration</h1>
          <p className="text-sm text-[#8B7355] mt-1">Per-branch discount tiers for near-expiry FG. Products within the tier's max_hours are auto-listed on the public deals page and available in POS.</p>
        </div>
        <Button variant="outline" size="sm" onClick={load} disabled={loading}>
          <RefreshCw className={`w-3.5 h-3.5 mr-1 ${loading ? "animate-spin" : ""}`} /> Refresh
        </Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {rows.map((r) => (
          <div key={r.id} className="bg-white border border-[#EAE5D9] rounded-lg p-4" data-testid={`deals-branch-${r.id}`}>
            <div className="flex items-start justify-between mb-2">
              <div>
                <div className="flex items-center gap-1.5 mb-0.5"><Store className="w-3.5 h-3.5 text-[#C05621]" /><span className="font-[Outfit] font-semibold">{r.name}</span></div>
                <div className="text-[10px] text-[#8B7355]">{r.code} · {r.type?.replace("_", " ")}</div>
              </div>
              <Badge className={`border-0 ${r.config?.enabled !== false ? "bg-emerald-100 text-emerald-800" : "bg-slate-100 text-slate-500"}`}>
                {r.config?.enabled !== false ? "Enabled" : "Disabled"}
              </Badge>
            </div>
            <div className="text-xs text-[#5C4033] mt-1 mb-3">
              {(r.config?.tiers || []).map((t, i) => (
                <span key={i} className="inline-block mr-2 mb-1 px-2 py-0.5 bg-[#FEF9EA] border border-amber-200 rounded text-[11px]">
                  ≤{t.max_hours}h → {t.discount_pct}% off
                </span>
              ))}
            </div>
            <div className="flex flex-wrap items-center gap-2 mt-2">
              <Button size="sm" variant="outline" onClick={() => openEdit(r)} data-testid={`deals-edit-${r.id}`}>Configure</Button>
              <Button size="sm" variant="ghost" onClick={() => copyUrl(r.code)} data-testid={`deals-copy-${r.id}`}><Copy className="w-3 h-3 mr-1" /> Copy public link</Button>
              <a href={`/deals/${r.code}`} target="_blank" rel="noreferrer" className="text-xs text-[#C05621] hover:underline flex items-center gap-1" data-testid={`deals-preview-${r.id}`}>
                <ExternalLink className="w-3 h-3" /> Preview
              </a>
            </div>
          </div>
        ))}
        {rows.length === 0 && !loading && (
          <div className="col-span-2 text-center text-[#8B7355] py-12">No branches configured.</div>
        )}
      </div>

      {/* Edit dialog */}
      <Dialog open={!!editing} onOpenChange={(v) => !v && setEditing(null)}>
        <DialogContent className="bg-white max-w-2xl">
          <DialogHeader>
            <DialogTitle className="font-[Outfit]">Deals for {editing?.branch_name}</DialogTitle>
          </DialogHeader>
          {editing && (
            <div className="space-y-4 pt-2">
              <label className="flex items-center gap-3 bg-[#FEF9EA] border border-amber-200 rounded p-3 cursor-pointer">
                <Switch checked={editing.enabled} onCheckedChange={(v) => setEditing({ ...editing, enabled: v })} data-testid="deals-form-enabled" />
                <div>
                  <div className="font-medium text-sm">Deals enabled</div>
                  <div className="text-xs text-[#8B7355]">Toggle off to hide this branch from the public deals page and POS auto-markdown.</div>
                </div>
              </label>

              <div>
                <div className="flex justify-between items-center mb-2">
                  <div className="uc-label !text-[#5C4033]">Discount Tiers</div>
                  <Button size="sm" variant="outline" onClick={addTier} data-testid="deals-add-tier"><Plus className="w-3 h-3 mr-1" /> Add tier</Button>
                </div>
                {editing.tiers.map((t, idx) => (
                  <div key={idx} className="grid grid-cols-12 gap-2 items-end mb-2" data-testid={`deals-tier-${idx}`}>
                    <div className="col-span-3">
                      <Label className="text-[10px]">Max hours to expiry</Label>
                      <Input type="number" min="1" max="168" value={t.max_hours} onChange={(e) => updateTier(idx, { max_hours: e.target.value })} />
                    </div>
                    <div className="col-span-3">
                      <Label className="text-[10px]">Discount %</Label>
                      <Input type="number" min="0" max="90" value={t.discount_pct} onChange={(e) => updateTier(idx, { discount_pct: e.target.value })} />
                    </div>
                    <div className="col-span-5">
                      <Label className="text-[10px]">Badge label</Label>
                      <Input value={t.label || ""} onChange={(e) => updateTier(idx, { label: e.target.value })} placeholder="e.g. Flash Deal · 50% off" />
                    </div>
                    <div className="col-span-1 flex justify-end">
                      <button className="text-red-500 hover:text-red-700 pb-2" onClick={() => removeTier(idx)}><Trash2 className="w-4 h-4" /></button>
                    </div>
                  </div>
                ))}
                {editing.tiers.length === 0 && <div className="text-xs text-[#8B7355] text-center py-3">No tiers. Add one to start.</div>}
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Min stock qty to publish</Label>
                  <Input type="number" min="1" value={editing.min_qty} onChange={(e) => setEditing({ ...editing, min_qty: e.target.value })} data-testid="deals-form-minqty" />
                </div>
                <div>
                  <Label>Branch tagline (optional)</Label>
                  <Input value={editing.note || ""} onChange={(e) => setEditing({ ...editing, note: e.target.value })} placeholder="Freshly baked daily since 1998" />
                </div>
              </div>

              <div className="text-xs bg-[#FDFBF7] border border-[#EAE5D9] rounded p-3">
                <b>Public URL:</b> <code className="text-[#C05621]">{window.location.origin}/deals/{editing.branch_code}</code>
                <div className="mt-1 text-[#8B7355]">Share this link, print as a QR code near your entrance, or embed on your Google Business page.</div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditing(null)}>Cancel</Button>
            <Button onClick={save} disabled={busy} className="bg-[#2C1810] text-white" data-testid="deals-form-save"><Save className="w-3.5 h-3.5 mr-1" /> Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
